import { useLazyQuery, useMutation } from '@apollo/client/react/hooks'
import { useTheme } from '@material-ui/core'
import { Form, Formik } from 'formik'
import { useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import AdotelSkeleton from '../../components/AdotelSkeleton'
import apolloClient from '../../graphql/apolloClient'
import {
  CREATE_PAYMENT_TERM_MUTATION,
  PAYMENT_TERM_QUERY
} from '../../graphql/walletPaymentTerm'
import { lang } from '../../localization'
import { PaymentTerm } from '../../types/walletPaymentTerm'
import { PaymentTermDetails } from './PaymentTermDetails'
import PaymentTermDetailsTable from './PaymentTermDetailsTable'
import { PaymentTermFooter } from './PaymentTermFooter'
import useStyles from './styles/PaymentTermMainForm.styles'
import { paymentTermFormValidationSchema } from './validations/paymentTermFormValidation'

const PAYMENT_TERM_FORM_INIT_VALUES: PaymentTerm = {
  name: '',
  code: '',
  displayPaymentTermCode: '',
  paymentTermCode: '',
  referenceTime: undefined,
  gracePeriodDays: 0,
  penaltyAdministrationFee: 0,
  earlyPaymentDiscountPercentage: 0,
  paymentTermDetails: [],
  proformaExpirationDuration: '0s',
  proformaPaymentDeadlineDuration: '0s'
}

const PaymentTermMainForm = () => {
  const classes = useStyles()
  const history = useHistory()
  const params = useParams<{ paymentTermID: string }>()
  const theme = useTheme()

  const [paymentTermOnSubmitFunction, { loading: loadingForPaymentTerm }] =
    useMutation(CREATE_PAYMENT_TERM_MUTATION, {
      notifyOnNetworkStatusChange: true
    })

  const [getInitialValues, { loading }] = useLazyQuery<{
    paymentTerm: PaymentTerm
  }>(PAYMENT_TERM_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    onError: (err) => {
      toast.error(err.message)
    }
  })

  const [initialValues, setinitialValues] = useState<PaymentTerm>()

  const getInitialValuesFunction = async () => {
    if (params.paymentTermID) {
      const { data, error } = await getInitialValues({
        variables: { paymentTermID: params.paymentTermID }
      })
      if (!error) {
        setinitialValues({
          ...data?.paymentTerm!
        })
      } else {
        toast.error(error.message)
      }
    }
  }

  const handleSubmit = async (values: PaymentTerm) => {
    const {
      paymentTermID,
      paymentTermDetails,
      displayPaymentTermCode,
      paymentTermCode,
      ...newVals
    } = values

    // Remove penaltyTermDetailID from all items in paymentTermDetails
    const updatedPaymentTermDetails = paymentTermDetails.map(
      ({ penaltyTermDetailID, ...detail }) => detail
    )

    // Construct the new values object without penaltyTermDetailID
    const newValues = {
      ...newVals,
      paymentTermDetails: updatedPaymentTermDetails
    }
    try {
      const { data: response } = await paymentTermOnSubmitFunction({
        variables: {
          input: newValues
        }
      })

      toast.success(response.createPaymentTerm.message)

      apolloClient.cache.evict({
        fieldName: 'paymentTerms'
      })
      history.push(`/${lang}/adopay/payment-terms`)
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message)
      } else {
        toast.error('ERROR!')
      }
    }
  }

  useEffect(() => {
    getInitialValuesFunction()
  }, [])

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues || PAYMENT_TERM_FORM_INIT_VALUES}
      onSubmit={handleSubmit}
      validationSchema={paymentTermFormValidationSchema}>
      {({
        values,
        setFieldValue,
        errors,
        touched,
        handleBlur,
        isSubmitting
      }) => {
        return (
          <>
            <Form>
              <PaymentTermDetails
                initialValues={initialValues || PAYMENT_TERM_FORM_INIT_VALUES}
                {...{
                  values,
                  setFieldValue,
                  errors,
                  touched,
                  handleBlur,
                  isSubmitting,
                  classes,
                  loading
                }}
              />
              <PaymentTermDetailsTable
                paymentTerms={values.paymentTermDetails} // Bind paymentTermDetails from values
                setPaymentTerms={(newDetails) =>
                  setFieldValue('paymentTermDetails', newDetails)
                } // Bind setFieldValue for updating paymentTermDetails in Formik state
              />
              {!!!params.paymentTermID && (
                <PaymentTermFooter
                  isSubmitting={isSubmitting}
                  loadingForPaymentTerm={loadingForPaymentTerm}
                />
              )}
            </Form>
            <AdotelSkeleton
              open={loading}
              circlesColor={theme.palette.primary.main}
              numberOfCircles={4}
            />
          </>
        )
      }}
    </Formik>
  )
}

export default PaymentTermMainForm
